import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import { GlobalContext } from '../../providers/GlobalProvider';
import {
  ExportHubAPI,
  DatabaseDashboard as Dash,
  LicenseUsageRow,
  HighRiskDriverKey,
  LICENSE_UTILIZATION_THRESHOLD,
} from 'api/ExportHubAPI';
import ChoroplethMap from './ChoroplethMap';
import { buildCountryValueMap, toAtlasCountry } from './mapCountries';
import { StatusPill, JurisdictionPill } from './productPresets';
import { formatCompactCurrency } from './licensePresets';

interface Props {
  databaseId: string;
}

const RISK_TONE: Record<string, string> = {
  Critical: 'eh-risk-critical',
  High: 'eh-risk-high',
  Medium: 'eh-risk-medium',
  Low: 'eh-risk-low',
};

// Why a product counts as high risk, and where the products table can show it.
// Drivers without a matching filter (screening risk, license coverage) are
// reported as plain counts rather than fake links.
const HIGH_RISK_DRIVERS: Record<
  HighRiskDriverKey,
  { label: string; hint: string; filter?: Record<string, string> }
> = {
  itar: {
    label: 'ITAR jurisdiction',
    hint: 'USML-controlled — every export needs State Department authorization.',
    filter: { jurisdiction: 'ITAR' },
  },
  nrc: {
    label: 'NRC jurisdiction',
    hint: 'Nuclear-controlled under 10 CFR — NRC licensing applies.',
    filter: { jurisdiction: 'NRC' },
  },
  restricted: {
    label: 'Restricted status',
    hint: 'Flagged as restricted — do not ship without review.',
    filter: { status: 'Restricted' },
  },
  screen_high: {
    label: 'High/Critical screening',
    hint: 'The latest completed screening came back High or Critical.',
  },
  uncovered_destination: {
    label: 'Uncovered destination',
    hint: 'Ships somewhere no linked license authorizes.',
  },
};

const pctLabel = (pct: number): string => `${Math.round(pct * 100)}%`;

/**
 * One license's utilization. The bar fills to `pct_used` with a tick at the 90%
 * threshold; over-drawn licenses clamp the bar at 100% and read in the danger
 * tone. A license with no recorded ceiling has nothing to fill, so it shows its
 * consumption as a plain figure instead of a misleading empty bar.
 */
const LicenseUsageBar: React.FC<{
  row: LicenseUsageRow;
  onClick: () => void;
}> = ({ row, onClick }) => {
  const pct = row.pct_used;
  const hasCeiling = pct !== null && row.value !== null;
  const over = hasCeiling && (pct as number) > 1;
  const near =
    hasCeiling && !over && (pct as number) >= LICENSE_UTILIZATION_THRESHOLD;
  const tone = over ? 'over' : near ? 'near' : 'ok';

  return (
    <button type='button' className='eh-lic-usage' onClick={onClick}>
      <span className='eh-lic-usage-head'>
        <span className='eh-lic-usage-name'>
          {row.license_number || row.license_type}
          {!!row.license_number && (
            <span className='eh-lic-usage-type'>{row.license_type}</span>
          )}
        </span>
        <span className='eh-lic-usage-products'>
          {row.linked_product_count} product
          {row.linked_product_count === 1 ? '' : 's'}
        </span>
      </span>

      {hasCeiling ? (
        <>
          <span className='eh-lic-usage-track'>
            <span
              className={`eh-lic-usage-fill ${tone}`}
              style={{ width: `${Math.min(pct as number, 1) * 100}%` }}
            />
            <span
              className='eh-lic-usage-tick'
              style={{ left: `${LICENSE_UTILIZATION_THRESHOLD * 100}%` }}
              title={`${pctLabel(LICENSE_UTILIZATION_THRESHOLD)} threshold`}
            />
          </span>
          <span className='eh-lic-usage-foot'>
            <span className='eh-lic-usage-figures'>
              {formatCompactCurrency(row.value_used)} used of{' '}
              {formatCompactCurrency(row.value)} ·{' '}
              {formatCompactCurrency(row.value_remaining)} left
            </span>
            <span className={`eh-lic-usage-pct ${tone}`}>
              {pctLabel(pct as number)}
              {over ? ' · over-drawn' : near ? ' · over threshold' : ''}
            </span>
          </span>
        </>
      ) : (
        <span className='eh-lic-usage-foot'>
          <span className='eh-lic-usage-figures'>
            {formatCompactCurrency(row.value_used)} used · no value ceiling on
            this license
          </span>
        </span>
      )}
    </button>
  );
};

type KpiTone = 'default' | 'success' | 'warning' | 'info';

const DbKpi: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  tone?: KpiTone;
  onClick?: () => void;
}> = ({ icon, label, value, tone = 'default', onClick }) => {
  const inner = (
    <>
      <span className='eh-dash-kpi-head'>
        {icon}
        <span className='eh-dash-kpi-label'>{label}</span>
      </span>
      <span className={`eh-dash-kpi-value ${tone}`}>{value}</span>
    </>
  );
  if (onClick) {
    return (
      <button type='button' className='eh-dash-kpi' onClick={onClick}>
        {inner}
      </button>
    );
  }
  return <div className='eh-dash-kpi'>{inner}</div>;
};

const DatabaseDashboard: React.FC<Props> = ({ databaseId }) => {
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const groupId = cur_user?.selected_team?.id;

  const [dash, setDash] = useState<Dash | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      setDash(await ExportHubAPI.getDatabaseDashboard(databaseId, groupId));
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [databaseId, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  const goProducts = (params: Record<string, string>): void => {
    const qs = new URLSearchParams(params).toString();
    history.push(
      `/export-hub/databases/${databaseId}/products${qs ? `?${qs}` : ''}`,
    );
  };
  const openProduct = (id: string): void =>
    history.push(`/export-hub/databases/${databaseId}/products/${id}`);
  const goLicenses = (): void =>
    history.push(`/export-hub/databases/${databaseId}/licenses`);

  if (loading) {
    return (
      <div className='export-hub-panel'>
        <div className='eh-table-skeleton'>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className='eh-skel eh-skel-row' />
          ))}
        </div>
      </div>
    );
  }
  if (!dash) {
    return (
      <div className='export-hub-panel'>
        <div className='export-hub-empty'>
          <div className='export-hub-empty-title'>Dashboard unavailable</div>
          <div className='export-hub-empty-sub'>
            We couldn&rsquo;t load this database&rsquo;s dashboard.
          </div>
        </div>
      </div>
    );
  }

  const { core } = dash;
  const highRisk = dash.high_risk;
  const destinationMap = buildCountryValueMap(
    dash.product_by_destination,
    (r) => r.count,
  );
  const destinationCount = destinationMap.size;
  const topDestinations = dash.product_by_destination.slice(0, 8);

  return (
    <div className='eh-dash eh-dash-db'>
      {/* KPI cluster (spec design: icon + label, value below) */}
      <div className='eh-dash-kpis'>
        <DbKpi
          icon={<Inventory2OutlinedIcon style={{ fontSize: 15 }} />}
          label='Products'
          value={core.total}
          onClick={() => goProducts({})}
        />
        <DbKpi
          icon={<CheckCircleOutlineIcon style={{ fontSize: 15 }} />}
          label='Compliant'
          value={core.compliant}
          tone='success'
          onClick={() => goProducts({ status: 'Compliant' })}
        />
        <DbKpi
          icon={<ReportProblemOutlinedIcon style={{ fontSize: 15 }} />}
          label='Action Required'
          value={core.action_required}
          tone='warning'
          onClick={() => goProducts({ status: 'Action Required' })}
        />
        <DbKpi
          icon={<EditOutlinedIcon style={{ fontSize: 15 }} />}
          label='Needs Input'
          value={core.needs_input}
          tone='warning'
          onClick={() => goProducts({ status: 'Needs Input' })}
        />
        <DbKpi
          icon={<DescriptionOutlinedIcon style={{ fontSize: 15 }} />}
          label='Active Licenses'
          value={core.active_licenses}
          onClick={goLicenses}
        />
        <DbKpi
          icon={<PaidOutlinedIcon style={{ fontSize: 15 }} />}
          label='Value Remaining'
          value={formatCompactCurrency(core.value_remaining)}
          tone='success'
          onClick={goLicenses}
        />
      </div>

      {/* License usage (wide) + expirations / open issues (rail) */}
      <div className='eh-dash-main'>
        <div className='eh-panel'>
          <div className='eh-panel-title eh-panel-title-flex'>
            <span>
              <DescriptionOutlinedIcon
                style={{ fontSize: 16, marginRight: 6, verticalAlign: 'middle' }}
              />
              License usage
            </span>
            <span className='eh-panel-note'>
              Threshold {pctLabel(LICENSE_UTILIZATION_THRESHOLD)}
            </span>
          </div>
          {dash.license_usage.length === 0 ? (
            <div className='eh-detail-note'>
              No licenses on this database yet.
            </div>
          ) : (
            <div className='eh-dash-scroll'>
              {dash.license_usage.map((l) => (
                <LicenseUsageBar key={l.id} row={l} onClick={goLicenses} />
              ))}
            </div>
          )}
        </div>

        <div className='eh-dash-side'>
          <div className='eh-panel'>
            <div className='eh-panel-title'>Upcoming expirations (90d)</div>
            {dash.expiring_licenses.length === 0 && (
              <div className='eh-detail-note'>Nothing expiring soon.</div>
            )}
            <div className='eh-dash-scroll'>
              {dash.expiring_licenses.map((l) => (
                <button
                  key={l.id}
                  type='button'
                  className='eh-exp-card'
                  onClick={goLicenses}
                >
                  <span className='eh-exp-top'>
                    <span className='eh-pill eh-pill-info'>{l.license_type}</span>
                    <span
                      className={`eh-exp-days ${
                        l.days_left <= 30 ? 'urgent' : ''
                      }`}
                    >
                      {l.days_left}d
                    </span>
                  </span>
                  <span className='eh-exp-num'>
                    {l.license_number || l.license_type}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className='eh-panel'>
            <div className='eh-panel-title'>Open issues</div>
            {dash.open_issues.length === 0 && (
              <div className='eh-detail-note'>No open issues.</div>
            )}
            <div className='eh-dash-scroll'>
              {dash.open_issues.map((p) => (
                <button
                  key={p.id}
                  type='button'
                  className='eh-feed-row'
                  onClick={() => openProduct(p.id)}
                >
                  <span className='eh-feed-main'>
                    <span className='eh-feed-title'>{p.name}</span>
                  </span>
                  <StatusPill value={p.compliance_status} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Export destinations (wide) + high-risk summary (rail) */}
      <div className='eh-dash-geo'>
        <div className='eh-panel'>
          <div className='eh-panel-title eh-panel-title-flex'>
            <span>
              <PublicOutlinedIcon
                style={{ fontSize: 16, marginRight: 6, verticalAlign: 'middle' }}
              />
              Export destinations
            </span>
            <span className='eh-panel-note'>
              {destinationCount} countr{destinationCount === 1 ? 'y' : 'ies'}
            </span>
          </div>
          {destinationCount === 0 ? (
            <div className='eh-detail-note'>
              No intended destinations recorded yet. Set them on a product, or
              include <code className='eh-code'>intended_destination_countries</code>{' '}
              in your next import.
            </div>
          ) : (
            <>
              <ChoroplethMap
                values={destinationMap}
                selected={[]}
                tooltipFor={(_c, v) =>
                  `${v} product${v === 1 ? '' : 's'} ship here`
                }
                onCountryClick={(atlas) => {
                  const row = dash.product_by_destination.find(
                    (r) => toAtlasCountry(r.country) === atlas,
                  );
                  if (row) goProducts({ dest: row.country });
                }}
              />
              <div className='eh-dest-legend'>
                {topDestinations.map((d) => (
                  <button
                    key={d.country}
                    type='button'
                    className='eh-dest-chip'
                    onClick={() => goProducts({ dest: d.country })}
                  >
                    {d.country}
                    <span className='eh-dest-chip-count'>{d.count}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className='eh-panel'>
          <div className='eh-panel-title eh-panel-title-flex'>
            <span>
              <GppMaybeOutlinedIcon
                style={{ fontSize: 16, marginRight: 6, verticalAlign: 'middle' }}
              />
              High-risk products
            </span>
            <span className='eh-panel-note'>
              {highRisk.total} of {core.total}
            </span>
          </div>

          {highRisk.total === 0 ? (
            <div className='eh-detail-note'>
              Nothing high risk — no ITAR/NRC jurisdictions, restricted
              products, high screening results, or uncovered destinations.
            </div>
          ) : (
            <>
              {/* Why they're flagged. A product often trips several drivers, so
                  these counts overlap and don't sum to the total. */}
              <div className='eh-risk-drivers'>
                {highRisk.drivers
                  .filter((d) => d.count > 0)
                  .map((d) => {
                    const meta = HIGH_RISK_DRIVERS[d.key];
                    if (!meta) return null;
                    const content = (
                      <>
                        <span className='eh-risk-driver-label'>
                          {meta.label}
                        </span>
                        <span className='eh-risk-driver-count'>{d.count}</span>
                      </>
                    );
                    return meta.filter ? (
                      <button
                        key={d.key}
                        type='button'
                        className='eh-risk-driver clickable'
                        title={meta.hint}
                        onClick={() =>
                          goProducts(meta.filter as Record<string, string>)
                        }
                      >
                        {content}
                      </button>
                    ) : (
                      <div
                        key={d.key}
                        className='eh-risk-driver'
                        title={meta.hint}
                      >
                        {content}
                      </div>
                    );
                  })}
              </div>

              <div className='eh-dash-scroll'>
                {highRisk.products.map((p) => (
                  <button
                    key={p.id}
                    type='button'
                    className='eh-risk-product'
                    onClick={() => openProduct(p.id)}
                  >
                    <span className='eh-risk-product-head'>
                      <span className='eh-feed-title'>{p.name}</span>
                      {!!p.risk && (
                        <span
                          className={`eh-risk-tag ${RISK_TONE[p.risk] || ''}`}
                        >
                          {p.risk}
                        </span>
                      )}
                    </span>
                    <span className='eh-risk-product-meta'>
                      <JurisdictionPill value={p.jurisdiction} />
                      <StatusPill value={p.compliance_status} />
                    </span>
                    <span className='eh-risk-product-why'>
                      {p.drivers
                        .map((k) => HIGH_RISK_DRIVERS[k]?.label)
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </button>
                ))}
                {highRisk.total > highRisk.products.length && (
                  <button
                    type='button'
                    className='eh-linkish'
                    onClick={() => goProducts({})}
                  >
                    View all {highRisk.total} in Products
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatabaseDashboard;
